import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellOff, Check, Clock, Loader2 } from "lucide-react";
import { API, SLOT_LABEL, auth, enablePush, isIosBrowser, sendTestReminder } from "./practiceApi";
import { ReminderTimes } from "./ReminderTimes";
import { HolidayPause, ReminderPreview } from "./ReminderExtras";
import { useAuth } from "../../hooks/useAuth";

const FAIL = {
  denied: "Notifications are blocked for Soulumo on this device. Allow them in your browser or phone settings, then try again.",
  unsupported: "This browser can't show reminders. On iPhone, add Soulumo to your Home Screen first and open it from there.",
};

// Reminders for the 21-day plan: on/off, turn on for this device, and a test nudge to prove it works.
export const ReminderLine = ({ p, onChange }) => {
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const [device, setDevice] = useState(false);
  const [editing, setEditing] = useState(false);
  const times = [...new Set([...(p.slots || []), ...(p.night ? ["night"] : [])])].filter((s) => p.times?.[s]).map((s) => `${SLOT_LABEL[s] || s} ${p.times[s]}`).join(" · ");
  const ios = isIosBrowser();
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("Notification" in window) || Notification.permission !== "granted") return;
    navigator.serviceWorker.getRegistration("/").then((r) => r?.pushManager.getSubscription()).then((s) => setDevice(!!s)).catch(() => {});
  }, []);
  const connect = async () => {
    const r = await enablePush().catch(() => "unsupported");
    if (r !== "ok") toast.error(FAIL[r] || FAIL.unsupported);
    if (r === "ok") setDevice(true);
    return r === "ok";
  };
  const run = (k, fn) => async () => { setBusy(k); try { await fn(); } catch { toast.error("Something went wrong. Please try again."); } setBusy(null); };
  const setOn = (on) => run(on ? "on" : "off", async () => {
    if (on && !(await connect())) return;
    const { data } = await axios.patch(`${API}/api/practice`, { reminders: on }, auth());
    onChange(data);
    toast.success(on ? "Device added. Gentle reminders will arrive on this device." : "Reminders are off.");
  })();
  const test = run("test", async () => {
    if (!(await connect())) return;
    const r = await sendTestReminder();
    if (r.sent > 0) toast.success("Test reminder sent. It should arrive in a few seconds.");
    else toast.error("We couldn't reach this device. Check notifications are allowed for Soulumo, then try again.");
  });
  const spin = (k) => busy === k && <Loader2 size={12} className="animate-spin" />;
  const btn = "inline-flex items-center gap-1.5 rounded-full border border-[#d7bb7a]/30 px-3 py-1.5 text-xs text-[#f3ecd9] transition-colors hover:bg-white/5 disabled:opacity-50";
  return (
    <div className="mt-5 rounded-2xl border border-[#2b3d5e] p-4" data-testid="practice-reminders">
      <p className="flex items-center gap-2 text-sm text-[#cfc6ae]" data-testid="practice-reminders-state">
        {p.reminders ? <Bell size={14} className="text-[#d7bb7a]" /> : <BellOff size={14} />}
        {p.paused ? "Reminders rest while your practice is paused." : p.reminders ? (p.reminder_devices ? (device ? "Gentle reminders are on for this device." : "Gentle reminders are on.") : "Reminders are on, but no device is connected yet. Tap \"Add this device\".") : "Reminders are off."}
      </p>
      {ios && <p className="mt-2 text-xs leading-relaxed text-[#9fb39a]" data-testid="practice-reminders-ios">On iPhone, reminders only work from the Home Screen app: tap Share, then "Add to Home Screen", open Soulumo from there and turn reminders on.</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {p.reminders ? (
          <>
            <button type="button" className={btn} disabled={!!busy} onClick={test} data-testid="practice-reminders-test">{spin("test")} Send a test reminder</button>
            {device && p.reminder_devices > 0              ? <span className="inline-flex items-center gap-1.5 rounded-full bg-[#7fb38a]/15 px-3 py-1.5 text-xs text-[#a9d6b2]" data-testid="practice-reminders-device-added"><Check size={12} /> Device added</span>
              : <button type="button" className={btn} disabled={!!busy} onClick={() => setOn(true)} data-testid="practice-reminders-device">{spin("on")} Add this device</button>}
            <button type="button" className={btn} disabled={!!busy} onClick={() => setOn(false)} data-testid="practice-reminders-off">{spin("off")} Turn off</button>
          </>
        ) : (
          <button type="button" className={btn} disabled={!!busy} onClick={() => setOn(true)} data-testid="practice-reminders-on">{spin("on")} Turn on reminders</button>
        )}
        {!editing && <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-[#d7bb7a] px-3 py-1.5 text-xs text-[#d7bb7a] transition-colors hover:bg-[#d7bb7a]/10" onClick={() => setEditing(true)} data-testid="practice-reminders-change-times"><Clock size={12} /> Change times</button>}
      </div>
      {!editing && times && <p className="mt-3 text-xs text-[#9a937f]" data-testid="practice-reminders-times">{times}</p>}
      {p.reminders && !p.paused && <HolidayPause p={p} onChange={onChange} />}
      <ReminderPreview p={p} title={user?.access_scope === "stand_easy_tester" ? "Stand Easy" : "Soulumo"} />
      {editing && <ReminderTimes p={p} onChange={onChange} onClose={() => setEditing(false)} />}
    </div>
  );
};
